import { useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { formatDate } from '@bookingdei/i18n/format-date';
import { useLanguage } from '../../shell/language-context';
import { CHART_H, areaPath, axisLabelIndices, branchLabel, chartTicks, chartY, linePath, seriesStats } from './home-model';
import type { DaySeries, Trend } from './home-model';

/** Rendered plot height in px (design CHART_H = 170; the SVG box is 640 × 120). */
const PLOT_PX = 170;
// Series colours in business order (design SERIES_C: navy, accent, series-2).
const SERIES_COLOR = ['var(--color-navy)', 'var(--color-accent)', 'var(--series-2)'];

const dateOf = (day: string) => new Date(`${day}T00:00:00`);

// "Bookings · last 30 days" (M:655-751): the 30-day total with the trend chip, series
// tabs (All + one per business) when there is more than one business, a smoothed area
// chart with y ticks, four date labels and a hover tooltip, and a legend card per
// series (total, share, average per day, best day). "Details" opens Insights, or the
// plan page when the plan has no insights entitlement.
export function HomeChart({
  series,
  businesses,
  trend,
  insightsLocked,
  showDetails = true,
}: {
  series: readonly DaySeries[];
  businesses: readonly { id: string; name: string }[];
  trend: Trend | null;
  insightsLocked: boolean;
  /** False in a team member's session: Insights and My Plan are owner pages (API-16). */
  showDetails?: boolean;
}) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [pick, setPick] = useState<string>('all');
  const [hover, setHover] = useState<number | null>(null);

  const multi = series.length > 1;
  const labelled = series.map((s, i) => ({
    ...s,
    color: multi ? SERIES_COLOR[i % SERIES_COLOR.length]! : 'var(--color-accent)',
    label: multi ? branchLabel(businesses.find((b) => b.id === s.businessId)?.name ?? '') : t('dc71.chart.single'),
  }));
  const visible = multi && pick !== 'all' ? labelled.filter((s) => s.businessId === pick) : labelled;
  const days = visible[0]?.days ?? [];
  const n = days.length;
  const total = visible.reduce((a, s) => a + s.values.reduce((x, y) => x + y, 0), 0);
  const max = Math.max(1, ...visible.flatMap((s) => s.values));
  const num = (v: number) => formatNumber(lang, v);

  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (r.width <= 0 || n < 2) return;
    const idx = Math.max(0, Math.min(n - 1, Math.round(((e.clientX - r.left) / r.width) * (n - 1))));
    if (idx !== hover) setHover(idx);
  };
  const hoverLeft = hover === null || n < 2 ? '0%' : `${((hover / (n - 1)) * 100).toFixed(2)}%`;

  return (
    <section className="hm-card hm-chart" aria-labelledby="hm-chart-title">
      <div className="hm-card__head">
        <h3 id="hm-chart-title" className="hm-card__title">
          {t('dc71.chart.title')}
        </h3>
        {showDetails && (
          <button
            type="button"
            className={insightsLocked ? 'hm-link hm-link--muted hm-push' : 'hm-link hm-push'}
            onClick={() => navigate(insightsLocked ? '/plan' : '/insights')}
          >
            {t('dc71.chart.details')} →
          </button>
        )}
      </div>
      <div className="hm-chart__body">
        <div className="hm-chart__top">
          <span className="hm-chart__total">{num(total)}</span>
          {trend && trend.up && <span className="hm-chart__trend">↗ {formatMessage(lang, 'dc71.kpi.pct', { n: trend.value })}</span>}
          {multi && (
            <span className="hm-chart__tabs" role="group" aria-label={t('dc71.chart.series')}>
              {[{ id: 'all', label: t('dc71.chart.all'), color: '' }, ...labelled.map((s) => ({ id: s.businessId ?? '', label: s.label, color: s.color }))].map((tb) => (
                <button
                  key={tb.id}
                  type="button"
                  data-chart-tab={tb.id}
                  aria-pressed={tb.id === pick}
                  className={tb.id === pick ? 'hm-chart__tab is-on' : 'hm-chart__tab'}
                  onClick={() => {
                    setPick(tb.id);
                    setHover(null);
                  }}
                >
                  {tb.color && <span className="hm-dot" style={{ background: tb.color }} />}
                  <span>{tb.label}</span>
                </button>
              ))}
            </span>
          )}
        </div>
        {n === 0 ? (
          <p className="hm-chart__empty">{t('dc71.chart.empty')}</p>
        ) : (
          <div className="hm-chart__plot">
            <div className="hm-chart__ticks" style={{ height: PLOT_PX }}>
              {chartTicks(max).map((v, i) => (
                <span key={i}>{num(v)}</span>
              ))}
            </div>
            <div className="hm-chart__area">
              <div className="hm-chart__hit" onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
                {hover !== null && (
                  <>
                    <span className="hm-chart__cursor" style={{ left: hoverLeft }} />
                    {visible.map((s) => (
                      <span
                        key={s.businessId ?? 'total'}
                        className="hm-chart__marker"
                        style={{ left: hoverLeft, top: `${((chartY(s.values[hover] ?? 0, max) / CHART_H) * PLOT_PX).toFixed(1)}px`, background: s.color }}
                      />
                    ))}
                    <span className="hm-chart__tip" style={{ left: hoverLeft }}>
                      <span className="hm-chart__tip-date">{formatDate(lang, dateOf(days[hover] ?? ''), { year: false })}</span>
                      {visible.map((s) => (
                        <span key={s.businessId ?? 'total'} className="hm-chart__tip-row">
                          <span className="hm-dot" style={{ background: s.color }} />
                          {formatMessage(lang, 'dc71.chart.point', { n: s.values[hover] ?? 0, label: s.label })}
                        </span>
                      ))}
                    </span>
                  </>
                )}
                <svg viewBox="0 0 640 120" preserveAspectRatio="none" className="hm-chart__svg" style={{ height: PLOT_PX }} aria-hidden="true">
                  {visible.map((s) => (
                    <path key={`a-${s.businessId ?? 'total'}`} d={areaPath(linePath(s.values, max))} fill={s.color} fillOpacity={visible.length > 1 ? 0.05 : 0.12} />
                  ))}
                  {visible.map((s) => (
                    <path
                      key={`l-${s.businessId ?? 'total'}`}
                      d={linePath(s.values, max)}
                      fill="none"
                      stroke={s.color}
                      strokeWidth={3}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  ))}
                </svg>
              </div>
              <div className="hm-chart__labels">
                {axisLabelIndices(n).map((i) => (
                  <span key={i}>{formatDate(lang, dateOf(days[i] ?? ''), { year: false })}</span>
                ))}
              </div>
            </div>
          </div>
        )}
        {n > 0 && (
          <div className="hm-chart__legend">
            {visible.map((s) => {
              const st = seriesStats(s.values);
              const grand = visible.reduce((a, x) => a + x.values.reduce((p, q) => p + q, 0), 0);
              return (
                <div key={s.businessId ?? 'total'} className="hm-legend">
                  <span className="hm-legend__label">
                    <span className="hm-dot" style={{ background: s.color }} />
                    {s.label}
                  </span>
                  <span className="hm-legend__fig">
                    <span className="hm-legend__total">{num(st.total)}</span>
                    {visible.length > 1 && (
                      <span className="hm-legend__share">{formatMessage(lang, 'dc71.kpi.pct', { n: grand ? Math.round((st.total / grand) * 100) : 0 })}</span>
                    )}
                  </span>
                  <span className="hm-legend__line">{formatMessage(lang, 'dc71.chart.avg', { n: st.avg })}</span>
                  <span className="hm-legend__line">
                    {formatMessage(lang, 'dc71.chart.best', {
                      n: st.best,
                      date: formatDate(lang, dateOf(s.days[st.bestIndex] ?? ''), { year: false }),
                    })}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
